import { SessionEndReason } from '@src/lib/session-redirect';
import { useSessionStore } from '@src/stores/use-session-store';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';

// Names the User this tab believes is signed in. The API answers a write with
// 409 session_mismatch when the session cookies now belong to someone else.
// Reads skip it: they would gain a CORS preflight and change nothing.
const EXPECTED_USER_HEADER = 'X-Max-User';
const READ_METHODS = ['get', 'head', 'options'];

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  if (config.headers) {
    config.headers.Accept = 'application/json';
  }
  if (config.url === '/users/sign_in') {
    config.headers.Accept = '*/*';
  }
  const { userUuid } = useSessionStore.getState();
  if (userUuid && !READ_METHODS.includes(config.method ?? 'get')) {
    config.headers[EXPECTED_USER_HEADER] = userUuid;
  }
  config.withCredentials = true;
  return config;
}

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
});

const responseInterceptor = (response: AxiosResponse) => {
  /* Reset session timeout on successful fetches */
  useSessionStore.getState().actions.restartSessionTimeout();
  return response;
};

// A burst of parallel requests all 401 at once (e.g. the dashboard's fan-out).
// Coalesce them onto a SINGLE in-flight refresh so we don't fire N concurrent
// /token/refresh calls — which, with server-side refresh-token rotation, would
// look like rapid reuse. The first 401 starts the refresh; the rest await it.
let refreshPromise: Promise<void> | null = null;

function refreshTokens(): Promise<void> {
  if (!refreshPromise) {
    const headers = import.meta.env.VITE_BASE_URL_BASIC_AUTH
      ? { Authorization: import.meta.env.VITE_BASE_URL_BASIC_AUTH }
      : {};

    refreshPromise = axios
      .post(`${import.meta.env.VITE_BASE_URL}/users/token/refresh`, undefined, {
        headers,
        withCredentials: true,
      })
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Return a never-resolving promise — the page is navigating away and we don't
// want downstream callers (loaders, query cache handlers) to see a rejection
// that could trigger extra error reporting or double-redirects.
export function markSessionDead(reason?: SessionEndReason): Promise<never> {
  useSessionStore.getState().actions.markDead(reason);
  return new Promise<never>(() => {});
}

function isSessionMismatch(error: AxiosError): boolean {
  const data = error.response?.data as { code?: unknown } | undefined;
  return error.status === 409 && data?.code === 'session_mismatch';
}

const errorInterceptor = async (error: AxiosError) => {
  const config = error.config as
    (AxiosRequestConfig & { _retriedAfterRefresh?: boolean }) | undefined;

  // Another User signed in from a different tab. Refreshing would only renew
  // their tokens, so end this tab's session straight away.
  if (isAxiosError(error) && isSessionMismatch(error)) {
    return markSessionDead('user_changed');
  }

  if (isAxiosError(error) && error.status === 401 && config) {
    // Already refreshed and retried once but still 401 → the session is dead.
    // Stop here rather than looping through another refresh.
    if (config._retriedAfterRefresh) {
      return markSessionDead();
    }
    config._retriedAfterRefresh = true;

    try {
      await refreshTokens();
      return api(config);
    } catch {
      return markSessionDead();
    }
  }
  throw error;
};

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(responseInterceptor, errorInterceptor);

if (import.meta.env.VITE_BASE_URL_BASIC_AUTH !== undefined) {
  api.defaults.headers.common['Authorization'] =
    import.meta.env.VITE_BASE_URL_BASIC_AUTH;
}
